import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

/**
 * U12 (issue #386): shared dialog wrapper, generalised from
 * core/ui/reason-prompt.component.ts. It keeps that component's working
 * parts (the open input, the unique aria-labelledby id, the
 * pendingFocus/ngAfterViewChecked move-in, the Escape HostListener) and
 * adds the focus trap while open, the focus return on close, the
 * max-height with an internally scrolling body and a sticky actions
 * region, and the body scroll lock (L02).
 *
 * The body is projected content. Buttons go into the [dialog-actions]
 * slot so the actions region stays visible while the body scrolls. The
 * wrapper owns only the dismissal: it emits closed on Escape and on a
 * backdrop click, and the adopter owns every other close path.
 *
 * Usage:
 *   <fap-dialog
 *     [open]="show()"
 *     [titleKey]="'i18n.fap.module.example.title'"
 *     (closed)="show.set(false)">
 *     <p>Body content</p>
 *     <div dialog-actions>
 *       <button type="button" (click)="close()">Cancel</button>
 *     </div>
 *   </fap-dialog>
 */
@Component({
  selector: 'fap-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    @if (open) {
      <div class="fap-dialog-overlay" (click)="dismiss()">
        <div #dialogEl class="fap-dialog" (click)="$event.stopPropagation()"
             role="dialog" aria-modal="true" [attr.aria-labelledby]="titleId"
             tabindex="-1" (keydown)="onDialogKeydown($event)">
          <h4 class="fap-dialog__title" [id]="titleId">{{ titleKey | translate }}</h4>
          <div class="fap-dialog__body">
            <ng-content />
          </div>
          <div class="fap-dialog__actions">
            <ng-content select="[dialog-actions]" />
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .fap-dialog-overlay {
      position: fixed; inset: 0;
      background: rgba(0, 0, 0, 0.4);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000;
    }
    .fap-dialog {
      background: var(--ob-surface);
      border-radius: 8px;
      padding: 20px 24px;
      max-width: 520px;
      width: 90%;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
      outline: none;
    }
    .fap-dialog__title { margin: 0 0 8px; font-size: 1.1rem; }
    .fap-dialog__body {
      overflow-y: auto;
      flex: 1 1 auto;
      min-height: 0;
    }
    .fap-dialog__actions {
      flex: 0 0 auto;
      position: sticky;
      bottom: 0;
      background: var(--ob-surface);
      padding-top: 8px;
      display: flex;
      gap: 8px;
      /* Full width so a projected gate footer (a space-between child with a
         border-top) can span the dialog width. */
      width: 100%;
      justify-content: flex-end;
    }
  `]
})
export class DialogComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @Input() open = false;
  @Input() titleKey = 'i18n.fap.common.dialog';
  @Output() closed = new EventEmitter<void>();

  // Unique id so aria-labelledby points at this dialog's title even when
  // several dialogs are mounted on one page (the reason-prompt pattern).
  readonly titleId = `fap-dialog-title-${Math.random().toString(36).slice(2, 10)}`;

  @ViewChild('dialogEl') private dialogEl?: ElementRef<HTMLDivElement>;

  // Set when open flips true; consumed by ngAfterViewChecked once the
  // dialog exists in the DOM (the @if renders it after the input changes).
  private pendingFocus = false;

  // The element that held focus when the dialog opened; focus goes back to
  // it when the dialog closes.
  private previousFocus: HTMLElement | null = null;

  // The inline body overflow value captured at open, restored exactly at
  // close and at destroy, because another open dialog may have set it.
  private previousBodyOverflow: string | null = null;

  // U12 review round: the open sequence. The most recently opened wrapper
  // owns the document-level dismissal, so nested dialogs close top-down and
  // one Escape press dismisses one dialog, not every open one.
  private static openIds = new Set<number>();
  private static nextId = 1;
  private openId = 0;

  private claimOpen(): void {
    this.openId = DialogComponent.nextId++;
    DialogComponent.openIds.add(this.openId);
  }

  private releaseOpen(): void {
    DialogComponent.openIds.delete(this.openId);
    this.openId = 0;
  }

  private isTopmost(): boolean {
    if (DialogComponent.openIds.size === 0) return true;
    let max = 0;
    DialogComponent.openIds.forEach(id => {
      if (id > max) max = id;
    });
    return this.openId >= max;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const o = changes['open'];
    if (!o) return;
    if (o.currentValue && !o.previousValue) {
      this.previousFocus = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.claimOpen();
      this.pendingFocus = true;
    } else if (!o.currentValue && o.previousValue) {
      this.releaseOpen();
      this.restoreBodyOverflow();
      this.restoreFocus();
    }
  }

  ngAfterViewChecked(): void {
    if (!this.pendingFocus || !this.open) return;
    const dialog = this.dialogEl?.nativeElement;
    if (!dialog) return;
    this.pendingFocus = false;
    const focusables = this.focusableElements(dialog);
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      dialog.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.openId) {
      // U12 review round: a destroy-mounted shell ([open]="true", or mounted
      // under a parent @if and removed whole on close) never sees the
      // false-flip in ngOnChanges, so this close path releases the open
      // sequence AND restores focus; without it the browser dumps focus on
      // the body.
      this.releaseOpen();
      this.restoreFocus();
    }
    this.restoreBodyOverflow();
  }

  // Escape closes the dialog, treated as dismissal. Guarded by open so the
  // listener is inert when the dialog is not shown, and by the open
  // sequence so one press dismisses the topmost dialog only.
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && this.isTopmost()) {
      this.dismiss();
    }
  }

  dismiss(): void {
    this.closed.emit();
  }

  // Focus trap: Tab and Shift+Tab cycle within the dialog's focusable
  // elements while it is open. Tab on the last element moves to the first,
  // Shift+Tab on the first moves to the last.
  onDialogKeydown(ev: KeyboardEvent): void {
    if (ev.key !== 'Tab' || !this.open) return;
    const dialog = this.dialogEl?.nativeElement;
    if (!dialog) return;
    const focusables = this.focusableElements(dialog);
    if (focusables.length === 0) {
      ev.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inside = active !== null && dialog.contains(active);
    // U12 review round 1: the dialog container itself is a reachable focus
    // target (tabindex="-1" for the heading announcement). Focus on it is
    // the boundary: Tab moves to the first element, Shift+Tab to the last,
    // so the browser default never sends focus behind the overlay.
    if (active === dialog) {
      ev.preventDefault();
      if (ev.shiftKey) {
        last.focus();
      } else {
        first.focus();
      }
      return;
    }
    if (ev.shiftKey) {
      if (active === first || !inside) {
        ev.preventDefault();
        last.focus();
      }
    } else if (active === last || !inside) {
      ev.preventDefault();
      first.focus();
    }
  }

  private focusableElements(dialog: HTMLElement): HTMLElement[] {
    return Array.from(
      dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter(el => !el.hasAttribute('disabled'));
  }

  private restoreBodyOverflow(): void {
    if (this.previousBodyOverflow === null) return;
    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = null;
  }

  // Focus return: the element captured at open gets focus back. A detached
  // element (removed while the dialog was open) falls back to the body.
  private restoreFocus(): void {
    const el = this.previousFocus;
    this.previousFocus = null;
    if (el && el.isConnected) {
      el.focus();
    } else {
      document.body.focus();
    }
  }
}
